import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import styled from 'styled-components';
import { Typography } from '../typography';
import { colors } from '../../const';
import { Card as CardBase, CardHeader } from './Components';

interface BarPanelProps {
  title: string;
  chartData: { name: string; times: string[]; values: number[] }[];
}

const Card = styled(CardBase)`
  grid-template-rows: auto auto 1fr;
`;

const Legend = styled.div`
  display: grid;
  grid-auto-flow: column;
  justify-content: center;
  align-items: center;
  column-gap: 2.4rem;
`;

const LegendItem = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  justify-content: start;
  column-gap: 0.8rem;
`;

const Swatch = styled.div<{ $color: string }>`
  width: 2rem;
  height: 1.2rem;
  border-radius: 0.2rem;
  background: ${({ $color }) => $color};
`;

const ChartWrap = styled.div`
  width: 100%;
  min-width: 0;
  min-height: 16rem;
  height: 100%;
  display: grid;
  box-sizing: border-box;
`;

const seriesPalette = [
  { key: 'series0', color: colors['--green-100'] },
  { key: 'series1', color: colors['--primary-100'] },
];

const BAR_CHART_INITIAL_DIMENSION = { width: 300, height: 160 } as const;

export const BarPanel = ({ title, chartData }: BarPanelProps) => {
  const series = chartData ?? [];
  const times = series[0]?.times ?? [];

  const rows = times.map((time, idx) => {
    const row: Record<string, string | number> = { time };
    series.forEach((s, sIdx) => {
      row[`series${sIdx}`] = s.values?.[idx] ?? 0;
    });
    return row;
  });

  return (
    <Card>
      <CardHeader title={title} />

      <Legend>
        {series.slice(0, 2).map((s, idx) => (
          <LegendItem key={`${s.name}-${idx}`}>
            <Swatch $color={seriesPalette[idx]?.color ?? colors['--gray-60']} />
            <Typography variant="textS" weight="normal" color="--gray-80" style={{ margin: 0 }}>
              {s.name}
            </Typography>
          </LegendItem>
        ))}
      </Legend>

      <ChartWrap>
        <ResponsiveContainer
          width="100%"
          height="100%"
          initialDimension={BAR_CHART_INITIAL_DIMENSION}
        >
          <BarChart
            data={rows}
            barSize={10}
            barGap={6}
            barCategoryGap={18}
            margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
          >
            <CartesianGrid stroke={colors['--gray-50']} vertical={false} />
            <XAxis
              dataKey="time"
              axisLine={false}
              tickLine={false}
              tick={{ fill: colors['--gray-70'], fontSize: 12 }}
            />
            <YAxis
              axisLine={{ stroke: colors['--gray-50'] }}
              tickLine={false}
              tick={{ fill: colors['--gray-70'], fontSize: 12 }}
              width={26}
            />
            <Tooltip cursor={{ fill: 'transparent' }} />

            {series.slice(0, 2).map((_, idx) => (
              <Bar
                key={`bar-${idx}`}
                dataKey={`series${idx}`}
                fill={seriesPalette[idx]?.color ?? colors['--gray-60']}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </ChartWrap>
    </Card>
  );
};
